<template>
  <div>
    <ProgressBar active-step="About you" />
    <div v-if="isManagedAccount">
      <PageHeading :default-heading="managedReturnToProposalHeader" />
      <div class="pvd-container container-divider">
        <p>
          {{ managedReturnToProposalBody }}
        </p>
        <!-- eslint-disable vue/no-v-html -->
        <p>
          <a
            :href="managedReturntoGoalProposalUrl"
            v-html="gbpLinkText"
          />
        </p>
        <p>
          <a
            :href="managedReturntoDifsProposalUrl"
            v-html="difsLinkText"
          />
        </p>
        <!--eslint-enable-->
      </div>
    </div>
    <div v-else>
      <PageHeading :default-heading="pageHeader" />
      <form
        ref="loginForm"
        autocomplete="off"
        method="post"
        novalidate="novalidate"
        :action="pd_envConfig.loginActionURL"
        @submit.prevent="handleFormSubmit()"
      >
        <!-- Toggle Component -->
        <div class="pvd-container container-divider">
          <h2
            id="existingCustomer"
            class="pvd-heading-L"
          >
            {{ header }}
          </h2>
          <ToggleComponent
            v-if="!youth"
            :id-prefix="idPrefix"
            :toggle-value.sync="pu_existingCustomer"
            :yes-label="pd_content.yes_toggle_text"
            :no-label="pd_content.no_toggle_text"
            :validation-rules="pd_content.existing_customer_validation_rules"
            :supplemental-row-text="pd_content.yesno_toggle_disclosure"
            :label="''"
            :track-events="true"
            :message="pd_content.existing_customer_validation_text"
            :show-internal-error-message="'true'"
          />
          <p
            v-if="youth"
            id="youth-disclosure"
            class="m-small-top"
          >
            {{ pd_content.youth_disclosure_text }}
            <a
              v-if="youth"
              id="youth-popover-trigger"
              href="javascript:void(0);"
              @click="track('login_disclosure_link', 'interaction')"
            >{{ pd_content.what_you_need_modal }}</a>
          </p>
          <ModalComponent
            v-if="youth"
            id-val="youth-popover"
            :modal-header="pd_content.what_you_need_header"
            :modal-list="[pd_content.what_you_need_bullet_one,
                          pd_content.what_you_need_bullet_two]"
            modal-class="small"
          />
        </div>

        <!-- Customer Section -->
        <div
          v-if="existingCustomer"
          ref="customer"
          :class="`${loginStyle}`"
        >
          <h2
            v-if="!youth"
            class="pvd-heading-L"
          >
            {{ pd_content.login_header }}
          </h2>
          <p
            v-if="!youth"
            class="m-small-top"
          >
            {{ pd_content.login_disclosure_text }}
            <a
              v-if="!youth"
              id="login-popover-trigger"
              href="javascript:void(0);"
              @click="track('login_disclosure_link', 'interaction')"
            >{{ pd_content.login_disclosure_link }}</a>{{ youth ? '' : '.' }}
            <ModalComponent
              v-if="!youth"
              id-val="login-popover"
              :modal-body1="pd_content.login_disclosure_link_popover"
              modal-class="small"
            />
          </p>
          <input
            type="hidden"
            name="AuthRedUrl"
            :value="authorizationUrl"
          >
          <div class="pvd-form-row">
            <div class="form-row-label">
              <label>{{ pd_content.username_label }}</label>
            </div>
            <div class="form-row-content">
              <input
                id="SSN"
                ref="SSN"
                v-validate="{required: true}"
                :class="['pvd-input', {'input-error': errors.has('SSN')}]"
                data-pii="true"
                name="SSN"
                type="text"
                value=""
                aria-required="true"
                :aria-describedby="errors.has('SSN') ? 'ssnError': null"
                :aria-invalid="errors.has('SSN') ? true: null"
                maxlength="32"
                data-vv-validate-on="blur"
              >
            </div>
            <InlineMessage
              v-show="errors.has('SSN')"
              :id-prefix="'ssn'"
              :message="pd_content.username_required_text"
            />
          </div>
          <div class="pvd-form-row">
            <div class="form-row-label">
              <label>{{ pd_content.password_label }}</label>
            </div>
            <div class="form-row-content">
              <input
                id="PIN"
                ref="PIN"
                v-validate="{required: true}"
                :class="['pvd-input', {'input-error': errors.has('PIN')}]"
                data-pii="true"
                name="PIN"
                type="password"
                value=""
                aria-required="true"
                :aria-invalid="errors.has('PIN') ? true: null"
                :aria-describedby="errors.has('PIN') ? 'pinError': null"
                maxlength="32"
                data-vv-validate-on="blur"
              >
            </div>
            <InlineMessage
              v-show="errors.has('PIN')"
              :id-prefix="'pin'"
              :message="pd_content.password_required_text"
            />
            <div
              v-if="needHelpText"
              class="form-row-supplemental"
            >
              <p class="xsmall">
                <a
                  :href="handleNeedLoggingClick"
                  target="_blank"
                  @click="track('need_logging_help', 'interaction')"
                >
                  {{ needHelpText }}
                  <pvd-icon
                    icon="new-tab"
                    class="m-small-left"
                  />
                </a>
              </p>
            </div>
          </div>
        </div>
        <!-- New To Fidelity Section Youth -->
        <div
          v-if="youth"
          class="pvd-container"
        >
          <h2>
            {{ pd_content.new_to_fidelity_url.header }}
          </h2>
          <p>
            {{ pd_content.new_to_fidelity_url.text }}
            <a
              :href="handleOpenNewAccountClick"
              target="_blank"
            >
              {{ newToFidelityText }}
            </a>
          </p>
        </div>

        <!-- Prospect Section -->
        <div
          v-if="pu_existingCustomer === 'No'"
          id="prospect-bullets"
          ref="Prospect"
          class="pvd-container container-divider"
        >
          <div>
            <h2
              class="pvd-heading-L"
            >
              {{ pd_content.prospect_header }}
            </h2>
            <p>
              {{ pd_content.prospect_disclosure_text }}
            </p>
            <ul class="pvd-list-bullet">
              <li>
                {{ pd_content.prospect_disclosure_bullet_one }}
                <a
                  id="tt-popover-trigger"
                  href="javascript:void(0);"
                  @click="track('detail_about_yourself', 'interaction')"
                >{{ pd_content.prospect_disclosure_bullet_link }}
                </a>
              </li>
              <li>
                {{ pd_content.prospect_disclosure_bullet_two }}
              </li>
              <li>
                {{ pd_content.prospect_disclosure_bullet_three }}
              </li>
            </ul>

            <ModalComponent
              id-val="tt-popover"
              :modal-header="pd_content.prospect_popover_bullet_heading"
              :modal-list="[pd_content.prospect_popover_bullet_one,
                            pd_content.prospect_popover_bullet_two,
                            pd_content.prospect_popover_bullet_three]"
              modal-class="small"
            />
          </div>
        </div>
        <!-- Navigation Section -->
        <NavigationComponent :is-customer="pu_existingCustomer" />
      </form>
    </div>
  </div>
</template>

<script>

import NavigationComponent from '../components/NavigationComponent.vue';
import ToggleComponent from '../components/ToggleComponent.vue';
import InlineMessage from '../components/InlineMessage.vue';
import ModalComponent from '../components/ModalComponent.vue';
import PageHeading from '../components/PageHeading.vue';
import dataHelper from '../mixins/dataHelper';
import utility from '../mixins/utility';
import validationHelper from '../mixins/validationHelper';
import ProgressBar from '../components/ProgressBar.vue';
import measurement from '../mixins/measurement';

export default {
  name: 'Login',
  components: {
    NavigationComponent,
    ToggleComponent,
    InlineMessage,
    PageHeading,
    ProgressBar,
    ModalComponent,
  },
  mixins: [dataHelper, utility, validationHelper, measurement],
  inject: ['$validator'],
  data() {
    return {
      idPrefix: 'existingCustomer',
      backButtonAttempt: false,
      windowLocation: window.location,
    };
  },
  computed: {
    existingCustomer() {
      return this.pu_existingCustomer === 'Yes';
    },
    managedReturnToProposalHeader() {
      return this.pd_content.login_page_managed.header;
    },
    managedReturnToProposalBody() {
      return this.pd_content.login_page_managed.body;
    },
    pageHeader() {
      if (this.pd_content.login_page_header) {
        document.title = this.setTitle(this.pd_content.login_page_header);
      }
      return this.pd_content.login_page_header;
    },
    handleNeedLoggingClick() {
      return this.pd_content.need_help_logging_in_url.url;
    },

    handleOpenNewAccountClick() {
      return this.pd_content.new_to_fidelity_url.url;
    },

    needHelpText() {
      return this.pd_content.need_help_logging_in_url.value;
    },

    newToFidelityText() {
      return this.pd_content.new_to_fidelity_url.anchor_text;
    },

    authorizationUrl() {
      const redirectQuery = '?AuthOrigUrl=';

      let redirectUrl = `${this.pd_envConfig.loginAuthzURL}${redirectQuery}${this.pd_envConfig.retailAccountSelectorURL}?rt=${this.routeId}`;

      Object.keys(this.queryParams).forEach((key) => {
        redirectUrl += `&${key}=${this.queryParams[key]}`;
      });

      if (this.pd_envConfig.supportedCustomers && this.pd_envConfig.supportedCustomers.includes(this.routeId)) {
        redirectUrl = `${this.pd_envConfig.loginAuthzURL}${redirectQuery}${this.windowLocation.origin}${this.windowLocation.pathname}`;

        if (Object.keys(this.queryParams).length > 0) {
          redirectUrl += '?';
          Object.keys(this.queryParams).forEach((key) => {
            redirectUrl += `${key}=${this.queryParams[key]}&`;
          });
        }
      }
      return redirectUrl;
    },
    youth() {
      const youth = this.routeId === 'youth';
      if (youth) {
        this.commitPrimaryUserData('Yes', 'existingCustomer');
      }
      return youth;
    },
    header() {
      return this.youth ? this.pd_content.youth_account_header : this.pd_content.already_customer;
    },
    loginStyle() {
      return this.youth ? 'pvd-container' : 'pvd-container container-divider';
    },
    isManagedAccount() {
      return this.isManaged(this.routeId);
    },
    gbpLinkText() {
      return this.pd_content.managed_login_exit_link.gbp_link_text;
    },
    difsLinkText() {
      return this.pd_content.managed_login_exit_link.difs_link_text;
    },
    managedReturntoGoalProposalUrl() {
      return this.pd_content.system_error_managed_exit_link.gbp_url;
    },
    managedReturntoDifsProposalUrl() {
      return this.pd_content.system_error_managed_exit_link.difs_url;
    },
  },
  mounted() {
    this.track(this.cs_stateName || 'login', 'pageview');
    this.removeModalClassIfExists();
    this.configureFirstPageBackButton(
      () => {
        if (this.pd_content.overlay_exit_link) {
          document.location = this.pd_content.overlay_exit_link.overview_url;
        }
      },
    );
  },
  methods: {
    handleFormSubmit() {
      this.$validator.validateScopes()
        .then((isValid) => {
          if (isValid) {
            this.$refs.loginForm.submit();
          } else if (this.errors && this.errors.items[0]) {
            if (this.errors.items[0].field === 'SSN') {
              this.$refs.SSN.focus();
            }
            if (this.errors.items[0].field === 'PIN') {
              this.$refs.PIN.focus();
            }
          }
        });
    },
  },
};
</script>

<style scoped>
</style>
